import { detectPlatform } from "../platform.js";

/**
 * Native bridge to the Android home-screen widgets (WidgetBridgePlugin).
 *
 * The web layer owns all data + theming, so it precomputes a JSON snapshot
 * (see `features/widgets/widget-snapshot.ts`) and pushes it here; the native
 * side encrypts it with an Android Keystore key, persists the ciphertext in
 * SharedPreferences, and broadcasts a refresh to every
 * placed widget. Fire-and-forget — a failure just leaves widgets on the last
 * published snapshot. No-op off Android.
 */
interface WidgetBridge {
  publish(options: { json: string }): Promise<void>;
}

let bridge: WidgetBridge | null = null;
let unsupported = false;

function get(): WidgetBridge | null {
  if (unsupported || detectPlatform() !== "capacitor") return null;
  if (!bridge) {
    try {
      // Capacitor injects window.Capacitor.registerPlugin at runtime; it returns
      // the native proxy synchronously (no async import needed).
      const core = (window as unknown as { Capacitor?: { registerPlugin?: (n: string) => WidgetBridge } }).Capacitor;
      if (!core?.registerPlugin) { unsupported = true; return null; }
      bridge = core.registerPlugin("WidgetBridge");
    } catch {
      unsupported = true;
      return null;
    }
  }
  return bridge;
}

/** Push a fresh widget snapshot to the native side (Android only, best-effort). */
export function publishWidgetSnapshot(json: string): void {
  const b = get();
  if (!b) return;
  void b.publish({ json }).catch(() => { /* widgets keep the previous snapshot */ });
}
